/**
 * What a failed callable says to the person who pressed the button.
 *
 * Pure, so it is tested without Firebase (test/callable-error.test.ts).
 * describeFirestoreError in errors.ts hands every `functions/*` error here.
 */

/** What a callable failure says when the server did not write a sentence for the reader. */
export const CALLABLE_FAILED =
  'That did not go through. Try again in a minute; if it keeps happening, tell support which page you were on.';

/*
 * The SDK writes its own message in three cases, and none of them is for
 * the reader: a network failure or an unhandled server error gives the
 * bare code ("internal", "INTERNAL"), a body it cannot parse gives
 * "Response is not valid JSON object." and an unknown status gives
 * "Unknown backend error status: ...". Firebase 12 also appends the HTTP
 * status to every message, ours included: "internal [0]" is what a
 * payment batch put in a red box, and "Amount must be positive. [400]" is
 * what a validation sentence looked like.
 */
const HTTP_STATUS_SUFFIX = /\s*\[\d{1,3}\]$/;
const BARE_CODE = /^[A-Za-z_-]+$/;
const SDK_OWN = /^(Response is (not valid JSON object|missing data field)|Unknown backend error status|Backend error status|Request was cancelled|Firebase Functions instance was deleted)/i;

/**
 * `code` is the callable code without the `functions/` prefix.
 *
 * Every code keeps the server's own sentence when there is one. The rule
 * that only invalid-argument, failed-precondition, permission-denied and
 * resource-exhausted may speak for themselves replaced sentences our own
 * callables throw on purpose under other codes: `internal` with "A link
 * to that file could not be made just now. Try again in a minute." and
 * with the accounting sync's instruction for reconnecting Xero,
 * `unavailable` with "Could not reach the payment provider. Try again
 * shortly." and `internal` with "The payment provider rejected the
 * request. Support has been notified.", which the generic sentence
 * contradicted by asking the reader to tell support. What the SDK writes
 * itself is never shown.
 */
export function describeCallableError(code: string, rawMessage: string | undefined): string {
  if (code === 'unauthenticated') return 'Sign in again to continue.';
  const message = (rawMessage ?? '').trim().replace(HTTP_STATUS_SUFFIX, '').trim();
  const fromServer = message !== '' && !BARE_CODE.test(message) && !SDK_OWN.test(message);
  if (fromServer) return message;
  if (code === 'permission-denied') return 'Your role cannot do this.';
  return CALLABLE_FAILED;
}
